import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import {
  Plus, Search, Edit2, Trash2, Eye, EyeOff, RefreshCw,
  Package, Save, X, Star, ChevronDown, ChevronUp, Minus,
  Euro, Tag, Truck, Gift, Image, Check, AlertCircle, Grid
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const TYPE_CONFIG = {
  location: { label: 'Location', color: 'var(--admin-teal)' },
  vente:    { label: 'Vente',    color: 'var(--admin-green)' },
  precommande: { label: 'Précommande', color: 'var(--admin-amber)' },
  fabrication: { label: 'Fabrication', color: 'var(--admin-purple)' },
};

// ──────────────────────────────────────────────────────────────────
// Sélecteur de produits avec recherche
// ──────────────────────────────────────────────────────────────────
function ProductSelector({ allProducts, selectedItems, onAdd }) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function close(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const filtered = allProducts.filter(p => {
    if (!search) return true;
    return p.nom.toLowerCase().includes(search.toLowerCase());
  });

  const selectedIds = selectedItems.map(i => i.product_id);

  return (
    <div ref={ref} className="relative">
      <div style={{ marginBottom: 20 }}>
        <p className="adm-eyebrow">Produits</p>
        <h2 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 26, fontWeight: 700,
          letterSpacing: '-.6px', color: 'var(--admin-text)', marginTop: 4 }}>Constructeur de packs</h2>
        <p style={{ color: 'var(--admin-muted)', fontSize: 13.5, marginTop: 3 }}>Packs combinant plusieurs produits</p>
      </div>

      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium"
        style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-teal-line)', color: 'var(--admin-teal)' }}
        data-testid="product-selector-btn"
      >
        <span className="flex items-center gap-2"><Plus size={15}/> Ajouter un produit au pack</span>
        {open ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
      </button>

      {open && (
        <div className="absolute left-0 right-0 z-50 rounded-xl mt-1 overflow-hidden"
          style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border)', maxHeight: 360, overflowY: 'auto', boxShadow: '0 16px 40px rgba(0,0,0,0.5)' }}>
          <div className="p-3 sticky top-0" style={{ background: 'var(--admin-surface)', borderBottom: '1px solid var(--admin-border-soft)' }}>
            <div className="relative">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--admin-faint)' }} />
              <Input
                autoFocus
                placeholder="Rechercher un produit..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9 text-sm"
                style={{ background: 'var(--admin-surface2)', borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }}
              />
            </div>
          </div>
          {filtered.length === 0 ? (
            <p className="text-center py-6 text-sm" style={{ color: 'var(--admin-faint)' }}>Aucun produit trouvé</p>
          ) : (
            filtered.map(p => {
              const already = selectedIds.includes(p.id);
              return (
                <button key={p.id}
                  onClick={() => { if (!already) { onAdd(p); setOpen(false); setSearch(''); } }}
                  disabled={already}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors hover:bg-white/5"
                  style={{ opacity: already ? 0.4 : 1, cursor: already ? 'default' : 'pointer' }}>
                  {p.images?.[0]
                    ? <img src={p.images[0]} alt="" className="w-9 h-9 rounded-lg object-cover flex-shrink-0" />
                    : <div className="w-9 h-9 rounded-lg flex-shrink-0" style={{ background: 'rgba(255,255,255,0.05)' }} />}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate" style={{ color: 'var(--admin-text)' }}>{p.nom}</p>
                    <p className="text-xs" style={{ color: 'var(--admin-faint)' }}>
                      {p.tarif_public > 0 ? `${p.tarif_public}€` : 'Sur devis'} ·
                      <span style={{ color: TYPE_CONFIG[p.type]?.color || '#888' }}> {TYPE_CONFIG[p.type]?.label}</span>
                    </p>
                  </div>
                  {already && <Check size={14} style={{ color: 'var(--admin-green)', flexShrink: 0 }} />}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Formulaire de création/édition de pack
// ──────────────────────────────────────────────────────────────────
function PackForm({ pack, allProducts, onSave, onCancel }) {
  const [form, setForm] = useState(() => pack ? {
    nom: pack.nom || '',
    description: pack.description || '',
    description_longue: pack.description_longue || '',
    image: pack.image || '',
    badge: pack.badge || '',
    badge_color: pack.badge_color || '#D4AF37',
    univers: pack.univers || [],
    prix_public: pack.prix_public || '',
    tarif_livraison: pack.tarif_livraison ?? 0,
    type_remise: pack.type_remise || 'pourcentage',
    remise_valeur: pack.remise_valeur || 0,
    contenu_offert: pack.contenu_offert || '',
    disponible: pack.disponible !== false,
    featured: pack.featured || false,
    order_weight: pack.order_weight || 50,
    items: pack.items || [],
  } : {
    nom: '', description: '', description_longue: '', image: '', badge: '', badge_color: 'var(--admin-gold)',
    univers: [], prix_public: '', tarif_livraison: 0, type_remise: 'pourcentage', remise_valeur: 0,
    contenu_offert: '', disponible: true, featured: false, order_weight: 50, items: [],
  });
  const [saving, setSaving] = useState(false);

  const token = sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');
  const H = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  function setF(key, val) { setForm(prev => ({ ...prev, [key]: val })); }

  function addProduct(p) {
    setForm(prev => ({
      ...prev,
      items: [...prev.items, {
        product_id: p.id,
        quantite: 1,
        inclus: true,
        prix_override: null,
        // data locale pour affichage
        _nom: p.nom,
        _type: p.type,
        _tarif: p.tarif_public,
        _image: p.images?.[0] || '',
      }]
    }));
  }

  function updateItem(index, key, val) {
    setForm(prev => {
      const items = [...prev.items];
      items[index] = { ...items[index], [key]: val };
      return { ...prev, items };
    });
  }

  function removeItem(index) {
    setForm(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  }

  function moveItem(index, dir) {
    setForm(prev => {
      const items = [...prev.items];
      const target = index + dir;
      if (target < 0 || target >= items.length) return prev;
      [items[index], items[target]] = [items[target], items[index]];
      return { ...prev, items };
    });
  }

  // Calcul automatique du prix
  const prixAuto = form.items.reduce((sum, item) => {
    if (!item.inclus) return sum;
    const tarif = item.prix_override !== null && item.prix_override !== '' && item.prix_override !== undefined
      ? parseFloat(item.prix_override) : (item._tarif || 0);
    return sum + tarif * (item.quantite || 1);
  }, 0);

  const prixAffiche = parseFloat(form.prix_public) || prixAuto;
  const remise = parseFloat(form.remise_valeur) || 0;
  let prixFinal = prixAffiche;
  if (form.type_remise === 'pourcentage' && remise > 0) prixFinal = prixAffiche * (1 - remise / 100);
  else if (form.type_remise === 'montant' && remise > 0) prixFinal = Math.max(0, prixAffiche - remise);
  else if (form.type_remise === 'prix_fixe' && remise > 0) prixFinal = remise;

  async function submit() {
    if (!form.nom.trim()) { toast.error('Le nom du pack est requis'); return; }
    if (form.items.length === 0) { toast.error('Ajoutez au moins un produit'); return; }
    setSaving(true);
    try {
      const body = {
        ...form,
        prix_public: parseFloat(form.prix_public) || null,
        tarif_livraison: parseFloat(form.tarif_livraison) || 0,
        remise_valeur: parseFloat(form.remise_valeur) || 0,
        items: form.items.map(item => ({
          product_id: item.product_id,
          quantite: parseInt(item.quantite) || 1,
          inclus: item.inclus,
          prix_override: item.prix_override !== null && item.prix_override !== '' ? parseFloat(item.prix_override) : null,
        })),
      };
      const method = pack ? 'PUT' : 'POST';
      const url = pack ? `${BACKEND}/api/admin/packs-custom/${pack.id}` : `${BACKEND}/api/admin/packs-custom`;
      const res = await fetch(url, { method, headers: H, body: JSON.stringify(body) });
      if (!res.ok) throw new Error();
      toast.success(pack ? 'Pack mis à jour !' : 'Pack créé !');
      onSave();
    } catch { toast.error('Erreur sauvegarde'); }
    setSaving(false);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto"
      style={{ backdropFilter: 'blur(6px)', background: 'rgba(0,0,0,0.8)' }}
      onClick={e => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="w-full max-w-3xl rounded-2xl my-4" style={{ background: 'var(--admin-bg)', border: '1px solid var(--admin-border)' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-5" style={{ borderBottom: '1px solid var(--admin-border-soft)' }}>
          <h2 className="font-bold text-xl" style={{ color: 'var(--admin-gold)' }}>{pack ? '✏️ Modifier le pack' : '✨ Créer un pack'}</h2>
          <button onClick={onCancel} style={{ color: 'var(--admin-muted)' }}><X size={20}/></button>
        </div>

        <div className="p-5 space-y-6 overflow-y-auto" style={{ maxHeight: '80vh' }}>
          {/* Infos générales */}
          <section>
            <h3 className="text-sm font-bold mb-3" style={{ color: 'var(--admin-gold)' }}>📋 Informations générales</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="text-xs mb-1 block" style={{ color: 'var(--admin-muted)' }}>Nom du pack *</label>
                <Input value={form.nom} onChange={e => setF('nom', e.target.value)}
                  placeholder="Ex: Pack Anniversaire Enfant Complet"
                  style={{ background: 'var(--admin-surface)', borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }} />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs mb-1 block" style={{ color: 'var(--admin-muted)' }}>Description courte</label>
                <textarea value={form.description} onChange={e => setF('description', e.target.value)}
                  rows={2} placeholder="Affichée dans les cartes..."
                  className="w-full text-sm px-3 py-2 rounded-lg resize-y"
                  style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border)', color: 'var(--admin-text)', outline: 'none' }} />
              </div>
              <div>
                <label className="text-xs mb-1 block" style={{ color: 'var(--admin-muted)' }}>Badge</label>
                <Input value={form.badge} onChange={e => setF('badge', e.target.value)}
                  placeholder="Bestseller, Populaire..." style={{ background: 'var(--admin-surface)', borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }} />
              </div>
              <div>
                <label className="text-xs mb-1 block" style={{ color: 'var(--admin-muted)' }}>Couleur badge</label>
                <div className="flex gap-2 items-center">
                  <input type="color" value={form.badge_color} onChange={e => setF('badge_color', e.target.value)} className="w-10 h-9 rounded cursor-pointer" style={{ background: 'none', border: 'none' }} />
                  <Input value={form.badge_color} onChange={e => setF('badge_color', e.target.value)}
                    style={{ background: 'var(--admin-surface)', borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }} />
                </div>
              </div>
            </div>
          </section>

          {/* Image */}
          <section>
            <h3 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: 'var(--admin-gold)' }}><Image size={14}/> Image du pack</h3>
            <div className="flex gap-3 items-start">
              <Input value={form.image} onChange={e => setF('image', e.target.value)}
                placeholder="https://... URL de l'image du pack"
                style={{ background: 'var(--admin-surface)', borderColor: 'var(--admin-border)', color: 'var(--admin-text)', flex: 1 }} />
              {form.image && (
                <img src={form.image} alt="aperçu" className="w-20 h-20 rounded-xl object-cover flex-shrink-0"
                  onError={e => e.target.style.display='none'} />
              )}
            </div>
            <p className="text-xs mt-1" style={{ color: 'var(--admin-faint)' }}>Collez l'URL d'une image Unsplash ou de votre hébergement</p>
          </section>

          {/* Produits du pack */}
          <section>
            <h3 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: 'var(--admin-teal)' }}>
              <Package size={14}/> Produits inclus dans le pack
              <span className="ml-1 px-2 py-0.5 rounded-full text-xs" style={{ background: 'rgba(93,188,191,0.15)', color: 'var(--admin-teal)' }}>{form.items.length} produit{form.items.length > 1 ? 's' : ''}</span>
            </h3>

            {/* Sélecteur */}
            <ProductSelector allProducts={allProducts} selectedItems={form.items} onAdd={addProduct} />

            {/* Liste des produits sélectionnés */}
            {form.items.length > 0 && (
              <div className="mt-3 space-y-2">
                {form.items.map((item, i) => {
                  // Trouver le produit dans allProducts pour affichage
                  const prod = allProducts.find(p => p.id === item.product_id);
                  const nom = item._nom || prod?.nom || item.product_id;
                  const type = item._type || prod?.type || 'location';
                  const image = item._image || prod?.images?.[0] || '';
                  const tarifBase = item._tarif || prod?.tarif_public || 0;
                  const typeCfg = TYPE_CONFIG[type] || TYPE_CONFIG.location;

                  return (
                    <div key={i} className="flex items-start gap-3 p-3 rounded-xl"
                      style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)' }}>
                      {/* Image + infos */}
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {image
                          ? <img src={image} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                          : <div className="w-10 h-10 rounded-lg flex-shrink-0" style={{ background: 'rgba(255,255,255,0.05)' }} />}
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate" style={{ color: 'var(--admin-text)' }}>{nom}</p>
                          <span className="text-xs" style={{ color: typeCfg.color }}>{typeCfg.label}</span>
                          {type === 'location' && (
                            <span className="ml-2 text-xs" style={{ color: 'var(--admin-amber)' }}>⚠ Dispo vérifiée</span>
                          )}
                        </div>
                      </div>

                      {/* Contrôles */}
                      <div className="flex items-center gap-2 flex-wrap justify-end">
                        {/* Quantité */}
                        <div className="flex items-center gap-1 rounded-lg px-1" style={{ border: '1px solid var(--admin-border)' }}>
                          <button onClick={() => updateItem(i, 'quantite', Math.max(1, (item.quantite||1)-1))} className="w-7 h-7 flex items-center justify-center" style={{ color: 'var(--admin-muted)' }}><Minus size={11}/></button>
                          <span className="w-6 text-center text-sm font-mono" style={{ color: 'var(--admin-text)' }}>{item.quantite||1}</span>
                          <button onClick={() => updateItem(i, 'quantite', (item.quantite||1)+1)} className="w-7 h-7 flex items-center justify-center" style={{ color: 'var(--admin-teal)' }}><Plus size={11}/></button>
                        </div>

                        {/* Prix override */}
                        <div className="flex items-center gap-1">
                          <input type="number" min="0" step="0.5"
                            value={item.prix_override !== null && item.prix_override !== undefined && item.prix_override !== '' ? item.prix_override : ''}
                            onChange={e => updateItem(i, 'prix_override', e.target.value === '' ? null : e.target.value)}
                            placeholder={tarifBase > 0 ? `${tarifBase}` : 'Prix'}
                            className="w-20 text-sm text-center px-2 py-1.5 rounded-lg"
                            style={{ background: 'var(--admin-surface2)', border: '1px solid rgba(212,175,55,0.25)', color: 'var(--admin-text)', outline: 'none' }} />
                          <span className="text-xs" style={{ color: 'var(--admin-faint)' }}>€</span>
                        </div>

                        {/* Inclus dans le prix */}
                        <button onClick={() => updateItem(i, 'inclus', !item.inclus)}
                          className="text-xs px-2 py-1 rounded-lg font-medium"
                          style={item.inclus
                            ? { background: 'rgba(31,157,122,0.15)', color: 'var(--admin-green)', border: '1px solid rgba(31,157,122,0.25)' }
                            : { background: 'rgba(255,255,255,0.05)', color: 'var(--admin-muted)', border: '1px solid var(--admin-border-soft)' }}>
                          {item.inclus ? '✓ Inclus' : 'En option'}
                        </button>

                        {/* Ordre */}
                        <div className="flex flex-col gap-0.5">
                          <button onClick={() => moveItem(i, -1)} disabled={i===0} style={{ color: i===0 ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.4)' }}><ChevronUp size={12}/></button>
                          <button onClick={() => moveItem(i, 1)} disabled={i===form.items.length-1} style={{ color: i===form.items.length-1 ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.4)' }}><ChevronDown size={12}/></button>
                        </div>

                        <button onClick={() => removeItem(i)} className="p-1.5 rounded" style={{ color: 'var(--admin-red)' }}><X size={13}/></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Récapitulatif prix auto */}
            {form.items.length > 0 && (
              <div className="mt-3 p-3 rounded-xl" style={{ background: 'var(--admin-gold-dim)', border: '1px solid rgba(212,175,55,0.15)' }}>
                <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>
                  Prix calculé automatiquement (produits inclus) : <strong style={{ color: 'var(--admin-gold)' }}>{prixAuto.toFixed(2)}€</strong>
                </p>
              </div>
            )}
          </section>

          {/* Tarification */}
          <section>
            <h3 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: 'var(--admin-gold)' }}><Euro size={14}/> Tarification du pack</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs mb-1 block" style={{ color: 'var(--admin-muted)' }}>Prix public (vide = calculé auto)</label>
                <div className="flex items-center gap-2">
                  <Input type="number" min="0" step="0.5" value={form.prix_public}
                    onChange={e => setF('prix_public', e.target.value)}
                    placeholder={prixAuto.toFixed(2)}
                    style={{ background: 'var(--admin-surface)', borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }} />
                  <span className="text-sm" style={{ color: 'var(--admin-muted)' }}>€</span>
                </div>
              </div>
              <div>
                <label className="text-xs mb-1 block" style={{ color: 'var(--admin-muted)' }}>Tarif livraison</label>
                <div className="flex items-center gap-2">
                  <Input type="number" min="0" step="1" value={form.tarif_livraison}
                    onChange={e => setF('tarif_livraison', e.target.value)}
                    style={{ background: 'var(--admin-surface)', borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }} />
                  <span className="text-sm" style={{ color: 'var(--admin-muted)' }}>€</span>
                </div>
              </div>
              <div>
                <label className="text-xs mb-1 block" style={{ color: 'var(--admin-muted)' }}>Type de remise</label>
                <select value={form.type_remise} onChange={e => setF('type_remise', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border)', color: 'var(--admin-text)' }}>
                  <option value="rien">Pas de remise</option>
                  <option value="pourcentage">Remise en % (ex: -10%)</option>
                  <option value="montant">Remise en montant (ex: -50€)</option>
                  <option value="prix_fixe">Prix fixe du pack</option>
                </select>
              </div>
              {form.type_remise !== 'rien' && (
                <div>
                  <label className="text-xs mb-1 block" style={{ color: 'var(--admin-muted)' }}>
                    {form.type_remise === 'pourcentage' ? 'Remise (%)' : form.type_remise === 'montant' ? 'Montant remise (€)' : 'Prix final du pack (€)'}
                  </label>
                  <Input type="number" min="0" step="0.5" value={form.remise_valeur}
                    onChange={e => setF('remise_valeur', e.target.value)}
                    style={{ background: 'var(--admin-surface)', borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }} />
                </div>
              )}
            </div>
            <div className="mt-3">
              <label className="text-xs mb-1 block" style={{ color: 'var(--admin-muted)' }}>Ce qui est offert / inclus (texte affiché au client)</label>
              <Input value={form.contenu_offert} onChange={e => setF('contenu_offert', e.target.value)}
                placeholder="Ex: Livraison + installation offerte, câbles et accessoires inclus..."
                style={{ background: 'var(--admin-surface)', borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }} />
            </div>

            {/* Preview prix final */}
            <div className="mt-3 flex items-center gap-4 p-3 rounded-xl" style={{ background: 'var(--admin-surface)', border: '1px solid rgba(212,175,55,0.2)' }}>
              <div className="text-center">
                <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>Prix public</p>
                <p className="text-lg font-bold" style={{ color: 'var(--admin-gold)' }}>{prixAffiche.toFixed(2)}€</p>
              </div>
              {prixFinal < prixAffiche && (
                <>
                  <span style={{ color: 'var(--admin-faint)' }}>→</span>
                  <div className="text-center">
                    <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>Prix final pack</p>
                    <p className="adm-title"  style={{ color: 'var(--admin-green)' }}>{prixFinal.toFixed(2)}€</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>Économie</p>
                    <p className="text-lg font-bold" style={{ color: 'var(--admin-amber)' }}>-{(prixAffiche - prixFinal).toFixed(2)}€</p>
                  </div>
                </>
              )}
              {form.tarif_livraison > 0 && (
                <div className="text-center">
                  <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>+ Livraison</p>
                  <p className="font-bold" style={{ color: 'var(--admin-blue)' }}>{parseFloat(form.tarif_livraison).toFixed(2)}€</p>
                </div>
              )}
            </div>
          </section>

          {/* Options */}
          <section>
            <h3 className="text-sm font-bold mb-3" style={{ color: 'var(--admin-blue)' }}>⚙️ Options</h3>
            <div className="flex gap-6 flex-wrap">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.disponible} onChange={e => setF('disponible', e.target.checked)} />
                <span className="text-sm" style={{ color: 'var(--admin-text)' }}>Visible sur le site</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.featured} onChange={e => setF('featured', e.target.checked)} />
                <span className="text-sm" style={{ color: 'var(--admin-text)' }}>Mise en avant (bestseller)</span>
              </label>
              <div className="flex items-center gap-2">
                <label className="text-xs" style={{ color: 'var(--admin-muted)' }}>Poids affichage</label>
                <Input type="number" value={form.order_weight} onChange={e => setF('order_weight', parseInt(e.target.value)||50)}
                  style={{ background: 'var(--admin-surface)', borderColor: 'var(--admin-border)', color: 'var(--admin-text)', width: 80 }} />
              </div>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-5" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <Button onClick={submit} disabled={saving} className="flex-1"
            style={{ background: 'rgba(212,175,55,0.2)', color: 'var(--admin-gold)', border: '1px solid rgba(212,175,55,0.3)' }}>
            {saving ? <RefreshCw size={14} className="animate-spin mr-2"/> : <Save size={14} className="mr-2"/>}
            {pack ? 'Sauvegarder les modifications' : 'Créer le pack'}
          </Button>
          <Button onClick={onCancel} variant="outline" style={{ borderColor: 'var(--admin-border)', color: 'var(--admin-muted)' }}>
            Annuler
          </Button>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Page principale AdminPacks
// ──────────────────────────────────────────────────────────────────
export default function AdminPacks() {
  const [packs, setPacks] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editPack, setEditPack] = useState(null);

  const token = sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');
  const H = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [packsRes, prodsRes] = await Promise.all([
        fetch(`${BACKEND}/api/admin/packs-custom`, { headers: H }).then(r => r.json()),
        fetch(`${BACKEND}/api/admin/packs-custom/produits/tous`, { headers: H }).then(r => r.json()),
      ]);
      setPacks(packsRes.packs || []);
      setAllProducts(prodsRes.products || []);
    } catch { toast.error('Erreur chargement'); }
    setLoading(false);
  // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); }, [load]);

  async function togglePack(packId) {
    try {
      const res = await fetch(`${BACKEND}/api/admin/packs-custom/${packId}/toggle`, { method: 'POST', headers: H });
      const data = await res.json();
      toast.success(data.disponible ? 'Pack visible' : 'Pack masqué');
      load();
    } catch { toast.error('Erreur'); }
  }

  async function deletePack(pack) {
    if (!window.confirm(`Supprimer le pack "${pack.nom}" ?`)) return;
    try {
      await fetch(`${BACKEND}/api/admin/packs-custom/${pack.id}`, { method: 'DELETE', headers: H });
      toast.success('Pack supprimé');
      load();
    } catch { toast.error('Erreur'); }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }} data-testid="admin-packs">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="adm-title"  style={{ color: 'var(--admin-gold)' }}>Packs événementiels</h1>
          <p className="text-sm" style={{ color: 'var(--admin-muted)' }}>Créez des packs combinant location et boutique · {packs.length} pack{packs.length > 1 ? 's' : ''}</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={load} variant="outline" size="sm" style={{ borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }}>
            <RefreshCw size={14} className="mr-1" /> Actualiser
          </Button>
          <Button onClick={() => { setEditPack(null); setShowForm(true); }} size="sm"
            style={{ background: 'rgba(212,175,55,0.2)', color: 'var(--admin-gold)', border: '1px solid rgba(212,175,55,0.3)' }}>
            <Plus size={14} className="mr-1" /> Créer un pack
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3].map(i => <Skeleton key={i} className="h-64 rounded-2xl" style={{ background: 'var(--admin-surface)' }} />)}
        </div>
      ) : packs.length === 0 ? (
        <div className="text-center py-20" style={{ color: 'var(--admin-faint)' }}>
          <Grid size={48} className="mx-auto mb-4 opacity-20" />
          <p className="text-lg font-semibold mb-1" style={{ color: 'var(--admin-muted)' }}>Aucun pack créé</p>
          <p className="text-sm mb-6">Créez votre premier pack en combinant vos produits.</p>
          <Button onClick={() => setShowForm(true)} style={{ background: 'rgba(212,175,55,0.2)', color: 'var(--admin-gold)' }}>
            <Plus size={14} className="mr-2" /> Créer mon premier pack
          </Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {packs.map(pack => (
            <div key={pack.id} className="rounded-2xl overflow-hidden"
              style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)', opacity: pack.disponible ? 1 : 0.5 }}>
              {/* Image */}
              {pack.image ? (
                <div className="relative h-44 overflow-hidden">
                  <img src={pack.image} alt={pack.nom} className="w-full h-full object-cover" />
                  {pack.badge && (
                    <span className="absolute top-2 left-2 text-xs font-bold px-2 py-0.5 rounded-full"
                      style={{ background: pack.badge_color || '#D4AF37', color: '#000' }}>{pack.badge}</span>
                  )}
                  {!pack.disponible && (
                    <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.5)' }}>
                      <span className="text-sm font-bold" style={{ color: 'var(--admin-red)' }}>MASQUÉ</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="h-32 flex items-center justify-center" style={{ background: 'rgba(212,175,55,0.05)' }}>
                  <Package size={32} style={{ color: 'rgba(212,175,55,0.3)' }} />
                </div>
              )}

              <div className="p-4">
                <h3 className="font-bold text-sm mb-1" style={{ color: 'var(--admin-text)' }}>{pack.nom}</h3>
                {pack.description && (
                  <p className="text-xs mb-3 line-clamp-2" style={{ color: 'var(--admin-muted)' }}>{pack.description}</p>
                )}

                {/* Produits */}
                <div className="flex flex-wrap gap-1 mb-3">
                  {(pack.items_detail || []).slice(0,4).map((item, i) => (
                    <span key={i} className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--admin-muted)' }}>
                      {item.product?.nom?.slice(0,20) || item.product_id}
                    </span>
                  ))}
                  {(pack.items || []).length > 4 && (
                    <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--admin-muted)' }}>
                      +{pack.items.length - 4}
                    </span>
                  )}
                </div>

                {/* Prix */}
                <div className="flex items-center gap-2 mb-3">
                  <span className="adm-title"  style={{ color: 'var(--admin-gold)' }}>{(pack.prix_final || 0).toFixed(2)}€</span>
                  {pack.economie > 0 && (
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--admin-green-dim)', color: 'var(--admin-green)' }}>
                      -{pack.economie.toFixed(2)}€
                    </span>
                  )}
                  {pack.tarif_livraison > 0 && (
                    <span className="text-xs" style={{ color: 'var(--admin-faint)' }}>+ {pack.tarif_livraison}€ liv.</span>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button onClick={() => togglePack(pack.id)}
                    className="p-2 rounded-lg flex-shrink-0"
                    style={{ background: pack.disponible ? 'rgba(31,157,122,0.1)' : 'rgba(255,255,255,0.05)', color: pack.disponible ? '#1F9D7A' : 'rgba(255,255,255,0.3)' }}>
                    {pack.disponible ? <Eye size={14}/> : <EyeOff size={14}/>}
                  </button>
                  <button onClick={() => { setEditPack(pack); setShowForm(true); }}
                    className="flex-1 flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-semibold"
                    style={{ background: 'var(--admin-blue-dim)', color: 'var(--admin-blue)' }}>
                    <Edit2 size={12}/> Modifier
                  </button>
                  <button onClick={() => deletePack(pack)}
                    className="p-2 rounded-lg flex-shrink-0"
                    style={{ background: 'var(--admin-red-dim)', color: 'var(--admin-red)' }}>
                    <Trash2 size={14}/>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Formulaire pack */}
      {showForm && (
        <PackForm
          pack={editPack}
          allProducts={allProducts}
          onSave={() => { setShowForm(false); setEditPack(null); load(); }}
          onCancel={() => { setShowForm(false); setEditPack(null); }}
        />
      )}
    </div>
  );
}
